import { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Pressable, TextInput, ActivityIndicator, Modal, Platform, TouchableWithoutFeedback, ScrollView } from 'react-native';
import { Picker } from '@react-native-picker/picker';
import WebScrollView from '../../components/WebScrollView';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DollarSign, FileText, CheckCircle2, Clock, AlertCircle, X, Search, ExternalLink, MapPin, Calendar, Building, User, Send, Plus, LayoutList, List, ChevronDown, ChevronRight } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { ServiceRequestExpense } from './types';
import { shadows } from '../../lib/styles';
import { sendExpensePaymentRequestEmail } from '../../lib/expensePaymentRequests';
import { buildExpenseContextFields, getExpenseContextLabel } from '../../lib/adminExpenses';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/constants';

type ExpenseStatus =
  | 'new'
  | 'needs_invoiced'
  | 'awaiting_payment'
  | 'homeowner_paid'
  | 'needs_contractor_payment'
  | 'complete'
  | 'all';

const VALID_EXPENSE_STATUS_FILTERS: ExpenseStatus[] = [
  'new',
  'needs_invoiced',
  'awaiting_payment',
  'homeowner_paid',
  'needs_contractor_payment',
  'complete',
  'all',
];

const parseExpenseStatusFilter = (value: string | null): ExpenseStatus => {
  if (value && VALID_EXPENSE_STATUS_FILTERS.includes(value as ExpenseStatus)) {
    return value as ExpenseStatus;
  }
  return 'all';
};

interface ExpenseWithRelations extends ServiceRequestExpense {
  home?: {
    id: string;
    address: string;
    city: string;
    state: string;
    owner?: {
      id: string;
      full_name: string;
    } | null;
  } | null;
  service_request?: {
    id: string;
    title: string | null;
  } | null;
  visit?: {
    id: string;
    scheduled_date: string | null;
    visit_type: string | null;
    internal_name: string | null;
  } | null;
  invoice?: {
    id: string;
    status: string;
  } | null;
}

interface SummaryStats {
  new: number;
  needs_invoiced: number;
  awaiting_payment: number;
  homeowner_paid: number;
  needs_contractor_payment: number;
  complete: number;
  total: number;
  homeowner_outstanding: number;
  contractor_outstanding: number;
}

interface ExpensePaymentRequestHistoryItem {
  id: string;
  expense_id: string;
  requested_at: string;
  requested_by: string | null;
  recipient_email: string;
  status: 'pending' | 'sent' | 'failed' | 'skipped';
  requester_name?: string | null;
}

const ExpensesTab = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [allExpenses, setAllExpenses] = useState<ExpenseWithRelations[]>([]);
  const [expenses, setExpenses] = useState<ExpenseWithRelations[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ExpenseStatus>(() =>
    parseExpenseStatusFilter(searchParams.get('status'))
  );
  const [homeownerFilter, setHomeownerFilter] = useState<string | null>(null);
  const fetchExpensesRequestId = useRef(0);
  const [homeowners, setHomeowners] = useState<Array<{ id: string; full_name: string }>>([]);
  const [summaryStats, setSummaryStats] = useState<SummaryStats>({
    new: 0,
    needs_invoiced: 0,
    awaiting_payment: 0,
    homeowner_paid: 0,
    needs_contractor_payment: 0,
    complete: 0,
    total: 0,
    homeowner_outstanding: 0,
    contractor_outstanding: 0,
  });
  
  // Modal states
  const [showHomeownerPaidModal, setShowHomeownerPaidModal] = useState(false);
  const [showContractorPaidModal, setShowContractorPaidModal] = useState(false);
  const [showPaymentRequestConfirmModal, setShowPaymentRequestConfirmModal] = useState(false);
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<ExpenseWithRelations | null>(null);
  const [expenseToRequestPayment, setExpenseToRequestPayment] = useState<ExpenseWithRelations | null>(null);
  const [expenseToCreateInvoiceFor, setExpenseToCreateInvoiceFor] = useState<ExpenseWithRelations | null>(null);
  const [invoiceDueDate, setInvoiceDueDate] = useState('');
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [homeownerPaymentFormData, setHomeownerPaymentFormData] = useState({
    payment_date: '',
    payment_method: 'credit_card',
    payment_notes: '',
  });
  const [contractorPaymentFormData, setContractorPaymentFormData] = useState({
    payment_date: '',
    payment_method: 'check',
    payment_notes: '',
  });
  const [updating, setUpdating] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [requestingPaymentExpenseId, setRequestingPaymentExpenseId] = useState<string | null>(null);
  const [paymentRequestHistoryByExpenseId, setPaymentRequestHistoryByExpenseId] = useState<Record<string, ExpensePaymentRequestHistoryItem[]>>({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingExpense, setCreatingExpense] = useState(false);
  const [availableHomes, setAvailableHomes] = useState<Array<{ id: string; address: string; city: string; state: string }>>([]);
  const [availableVisits, setAvailableVisits] = useState<Array<{ id: string; scheduled_date: string | null; visit_type: string | null; internal_name: string | null }>>([]);
  const [createFormData, setCreateFormData] = useState({
    home_id: '',
    visit_id: '',
    amount: '',
    description: '',
    expense_date: new Date().toISOString().split('T')[0],
    category: '',
    vendor: '',
    billable_to_homeowner: true,
    needs_contractor_payment: true,
  });
  const [viewMode, setViewMode] = useState<'default' | 'compact'>('compact');
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  // Read status from URL
  useEffect(() => {
    const statusFromUrl = parseExpenseStatusFilter(searchParams.get('status'));
    setStatusFilter((current) => (current === statusFromUrl ? current : statusFromUrl));
  }, [searchParams]);

  // Update URL when status changes
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (statusFilter !== 'all') {
      newParams.set('status', statusFilter);
    } else {
      newParams.delete('status');
    }
    setSearchParams(newParams, { replace: true });
  }, [statusFilter]);

  useEffect(() => {
    fetchHomeowners();
  }, []);

  useEffect(() => {
    if (showCreateModal) {
      void fetchAvailableHomes();
    }
  }, [showCreateModal]);

  useEffect(() => {
    void fetchAvailableVisits(createFormData.home_id);
  }, [createFormData.home_id]);

  useEffect(() => {
    fetchExpenses();
  }, [statusFilter, homeownerFilter]);

  // Debounce search query
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchExpenses();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const getChildLineItems = (
    parentExpenseId: string,
    expensesSource: ExpenseWithRelations[] = allExpenses,
  ) => expensesSource.filter((expense) => expense.parent_expense_id === parentExpenseId);

  const getVisibleChildLineItems = (parentExpenseId: string) =>
    allExpenses.filter((expense) => expense.parent_expense_id === parentExpenseId && expense.homeowner_visible);

  const getChildrenNeedingContractorPayment = (
    parentId: string,
    expensesSource: ExpenseWithRelations[] = allExpenses,
  ) => getChildLineItems(parentId, expensesSource).filter((expense) => expense.needs_contractor_payment === true);

  const expenseRequiresContractorPayment = (
    expense: ExpenseWithRelations,
    expensesSource: ExpenseWithRelations[] = allExpenses,
  ) => {
    if (expense.parent_expense_id) {
      return expense.needs_contractor_payment === true;
    }
    return expense.needs_contractor_payment === true
      || getChildrenNeedingContractorPayment(expense.id, expensesSource).length > 0;
  };

  const isContractorPaid = (
    expense: ExpenseWithRelations,
    expensesSource: ExpenseWithRelations[] = allExpenses,
  ) => {
    const childrenNeedingPayment = expense.parent_expense_id
      ? []
      : getChildrenNeedingContractorPayment(expense.id, expensesSource);

    if (expense.needs_contractor_payment === true && expense.contractor_paid !== true) {
      return false;
    }

    if (childrenNeedingPayment.length > 0) {
      return childrenNeedingPayment.every((child) => child.contractor_paid === true);
    }

    return true;
  };

  const expenseNeedsContractorPayment = (
    expense: ExpenseWithRelations,
    expensesSource: ExpenseWithRelations[] = allExpenses,
  ) => {
    if (expense.parent_expense_id) return false;
    return expenseRequiresContractorPayment(expense, expensesSource)
      && !isContractorPaid(expense, expensesSource);
  };

  const getExpenseStatus = (
    expense: ExpenseWithRelations,
    expensesSource: ExpenseWithRelations[] = allExpenses,
  ): ExpenseStatus => {
    const homeownerDone = !expense.billable_to_homeowner || expense.is_paid;
    const contractorDone = !expenseRequiresContractorPayment(expense, expensesSource)
      || isContractorPaid(expense, expensesSource);

    if (homeownerDone && contractorDone) return 'complete';

    if (expense.billable_to_homeowner && !expense.is_paid) {
      if (!expense.invoice_requested && !expense.invoice_id) return 'new';
      if (expense.invoice_requested && !expense.invoice_id) return 'needs_invoiced';
      if (expense.invoice_id) return 'awaiting_payment';
    }

    if (!expense.billable_to_homeowner && expenseRequiresContractorPayment(expense, expensesSource) && !isContractorPaid(expense, expensesSource)) {
      return 'needs_contractor_payment';
    }

    if (expense.is_paid && expenseRequiresContractorPayment(expense, expensesSource) && !isContractorPaid(expense, expensesSource)) {
      return 'homeowner_paid';
    }

    return 'new';
  };

  const fetchHomeowners = async () => {
    try {
      const { data: expensesData, error: expensesError } = await supabase
        .from('service_request_expenses')
        .select('home_id')
        .not('home_id', 'is', null)
        .limit(10000);

      if (expensesError) throw expensesError;

      const homeIds = [...new Set((expensesData || []).map((e) => e.home_id).filter(Boolean))] as string[];

      if (homeIds.length === 0) {
        setHomeowners([]);
        return;
      }

      const { data: homesData, error: homesError } = await supabase
        .from('homes')
        .select(`
          id,
          owner:profiles!homes_owner_id_fkey (
            id,
            full_name
          )
        `)
        .in('id', homeIds);

      if (homesError) throw homesError;
      const homeownerMap = new Map<string, { id: string; full_name: string }>();

      (homesData || []).forEach((home: any) => {
        const owner = Array.isArray(home?.owner) ? home.owner[0] : home?.owner;
        if (owner?.id && owner?.full_name) {
          homeownerMap.set(owner.id, { id: owner.id, full_name: owner.full_name });
        }
      });

      setHomeowners(Array.from(homeownerMap.values()).sort((a, b) => 
        a.full_name.localeCompare(b.full_name)
      ));
    } catch (err: any) {
      console.error('Error fetching homeowners:', err);
    }
  };

  const fetchAvailableHomes = async () => {
    const { data, error: fetchError } = await supabase
      .from('homes')
      .select('id, address, city, state')
      .order('address', { ascending: true });
    if (fetchError) throw fetchError;
    setAvailableHomes(data || []);
  };

  const fetchAvailableVisits = async (homeId: string) => {
    if (!homeId) {
      setAvailableVisits([]);
      return;
    }
    const { data, error: fetchError } = await supabase
      .from('visits')
      .select('id, scheduled_date, visit_type, internal_name')
      .eq('home_id', homeId)
      .order('scheduled_date', { ascending: false });
    if (fetchError) throw fetchError;
    setAvailableVisits(data || []);
  };

  const fetchPaymentRequestHistory = async (expenseIds: string[]) => {
    if (expenseIds.length === 0) {
      setPaymentRequestHistoryByExpenseId({});
      return;
    }

    try {
      const { data: requestRows, error: requestError } = await supabase
        .from('expense_payment_requests')
        .select('id, expense_id, requested_at, requested_by, recipient_email, status')
        .in('expense_id', expenseIds)
        .order('requested_at', { ascending: false });

      if (requestError) throw requestError;

      const requesterIds = Array.from(
        new Set(
          (requestRows || [])
            .map((row) => row.requested_by)
            .filter((id): id is string => Boolean(id))
        )
      );

      let requesterNameMap = new Map<string, string>();
      if (requesterIds.length > 0) {
        const { data: requesterProfiles, error: requesterError } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', requesterIds);

        if (requesterError) throw requesterError;

        requesterNameMap = new Map(
          (requesterProfiles || []).map((profile) => [profile.id, profile.full_name || 'Unknown'])
        );
      }

      const historyMap: Record<string, ExpensePaymentRequestHistoryItem[]> = {};
      (requestRows || []).forEach((row) => {
        const item: ExpensePaymentRequestHistoryItem = {
          ...row,
          status: (row.status || 'pending') as ExpensePaymentRequestHistoryItem['status'],
          requester_name: row.requested_by ? requesterNameMap.get(row.requested_by) || null : null,
        };
        if (!historyMap[row.expense_id]) {
          historyMap[row.expense_id] = [];
        }
        historyMap[row.expense_id].push(item);
      });

      setPaymentRequestHistoryByExpenseId(historyMap);
    } catch (historyError) {
      console.error('Error loading expense payment request history:', historyError);
    }
  };

  const fetchExpenses = async () => {
    const requestId = ++fetchExpensesRequestId.current;

    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('service_request_expenses')
        .select(`
          *,
          home:homes (
            id,
            address,
            city,
            state,
            owner:profiles!homes_owner_id_fkey (
              id,
              full_name
            )
          ),
          service_request:service_requests (
            id,
            title
          ),
          visit:visits (
            id,
            scheduled_date,
            visit_type,
            internal_name
          ),
          invoice:invoices (
            id,
            status
          )
        `)
        .order('created_at', { ascending: false });

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      const expensesWithStatus = (data || []).map((expense: any) => {
        const serviceRequest = Array.isArray(expense.service_request) ? expense.service_request[0] : expense.service_request;
        const home = Array.isArray(expense.home) ? expense.home[0] : expense.home;
        const owner = home?.owner ? (Array.isArray(home.owner) ? home.owner[0] : home.owner) : null;
        const visit = Array.isArray(expense.visit) ? expense.visit[0] : expense.visit;
        
        return {
          ...expense,
          service_request: serviceRequest || null,
          home: home ? { ...home, owner } : null,
          visit: visit || null,
          invoice: Array.isArray(expense.invoice) 
            ? expense.invoice[0] 
            : expense.invoice,
        };
      }) as ExpenseWithRelations[];

      if (requestId !== fetchExpensesRequestId.current) {
        return;
      }

      setAllExpenses(expensesWithStatus);

      // Filter parent expenses only for top-level actions and reporting
      let filteredExpenses = expensesWithStatus.filter((expense) => !expense.parent_expense_id);
      if (statusFilter !== 'all') {
        filteredExpenses = expensesWithStatus.filter(expense => {
          if (expense.parent_expense_id) return false;
          if (statusFilter === 'needs_contractor_payment') {
            return expenseNeedsContractorPayment(expense, expensesWithStatus);
          }
          const status = getExpenseStatus(expense, expensesWithStatus);
          return status === statusFilter;
        });
      }

      // Filter by homeowner
      if (homeownerFilter) {
        filteredExpenses = filteredExpenses.filter(expense => {
          return expense.home?.owner?.id === homeownerFilter;
        });
      }

      // Filter by search query
      if (searchQuery.trim()) {
        const queryLower = searchQuery.toLowerCase();
        filteredExpenses = filteredExpenses.filter(expense => {
          return (
            expense.vendor?.toLowerCase().includes(queryLower) ||
            expense.description?.toLowerCase().includes(queryLower) ||
            expense.category?.toLowerCase().includes(queryLower) ||
            expense.service_request?.title?.toLowerCase().includes(queryLower) ||
            expense.home?.address?.toLowerCase().includes(queryLower) ||
            expense.home?.owner?.full_name?.toLowerCase().includes(queryLower) ||
            expense.visit?.internal_name?.toLowerCase().includes(queryLower)
          );
        });
      }

      setExpenses(filteredExpenses);
      await fetchPaymentRequestHistory(filteredExpenses.map((expense) => expense.id));

      // Calculate summary stats from FILTERED expenses (not all expenses)
      const stats: SummaryStats = {
        new: 0,
        needs_invoiced: 0,
        awaiting_payment: 0,
        homeowner_paid: 0,
        needs_contractor_payment: 0,
        complete: 0,
        total: 0,
        homeowner_outstanding: 0,
        contractor_outstanding: 0,
      };

      filteredExpenses.forEach(expense => {
        const status = getExpenseStatus(expense, expensesWithStatus);
        if (status !== 'needs_contractor_payment') {
          stats[status] += expense.amount;
        }
        if (expenseNeedsContractorPayment(expense, expensesWithStatus)) {
          stats.needs_contractor_payment += expense.amount;
        }
        stats.total += expense.amount;

        if (expense.billable_to_homeowner && !expense.is_paid) {
          stats.homeowner_outstanding += expense.amount;
        }

        if (expenseNeedsContractorPayment(expense, expensesWithStatus)) {
          stats.contractor_outstanding += expense.amount;
        }
      });

      setSummaryStats(stats);
    } catch (err: any) {
      if (requestId !== fetchExpensesRequestId.current) {
        return;
      }
      console.error('Error fetching expenses:', err);
      setError(err.message || 'Failed to load expenses');
    } finally {
      if (requestId === fetchExpensesRequestId.current) {
        setLoading(false);
      }
    }
  };

  const handleOpenPaymentRequestConfirmModal = (expense: ExpenseWithRelations) => {
    if (expense.parent_expense_id || !expense.billable_to_homeowner || expense.is_paid) return;
    const homeownerId = expense.home?.owner?.id;
    if (!homeownerId) {
      setError('Missing homeowner information for this expense');
      return;
    }
    setExpenseToRequestPayment(expense);
    setShowPaymentRequestConfirmModal(true);
  };

  const handleClosePaymentRequestConfirmModal = () => {
    setShowPaymentRequestConfirmModal(false);
    setExpenseToRequestPayment(null);
  };

  const handleOpenCreateInvoiceModal = (expense: ExpenseWithRelations) => {
    if (expense.parent_expense_id || !expense.billable_to_homeowner || expense.is_paid || expense.invoice_id) return;
    if (!expense.home?.owner?.id) {
      setError('Missing homeowner information for this expense');
      return;
    }
    const defaultDueDate = new Date();
    defaultDueDate.setDate(defaultDueDate.getDate() + 30);
    setInvoiceDueDate(defaultDueDate.toISOString().split('T')[0]);
    setInvoiceNotes('');
    setExpenseToCreateInvoiceFor(expense);
    setShowCreateInvoiceModal(true);
  };

  const handleCloseCreateInvoiceModal = () => {
    setShowCreateInvoiceModal(false);
    setExpenseToCreateInvoiceFor(null);
    setInvoiceNotes('');
  };

  const handleCreateInvoice = async () => {
    if (!expenseToCreateInvoiceFor) return;

    const homeownerId = expenseToCreateInvoiceFor.home?.owner?.id;
    if (!homeownerId) {
      setError('Missing homeowner information for this expense');
      return;
    }

    try {
      setCreatingInvoice(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const functionUrl = `${SUPABASE_URL}/functions/v1/create-stripe-invoice`;
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          homeownerId,
          expenseIds: [expenseToCreateInvoiceFor.id],
          dueDate: invoiceDueDate || undefined,
          notes: invoiceNotes || undefined,
        }),
      });

      const data = await response.json();
      if (!response.ok || data.error) {
        throw new Error(data.error || 'Failed to create invoice');
      }

      handleCloseCreateInvoiceModal();
      await fetchExpenses();
    } catch (createError: any) {
      console.error('Error creating invoice:', createError);
      setError(createError.message || 'Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleConfirmRequestHomeownerPayment = async () => {
    if (!expenseToRequestPayment) return;
    const homeownerId = expenseToRequestPayment.home?.owner?.id;
    const homeownerName = expenseToRequestPayment.home?.owner?.full_name || null;

    if (!homeownerId) {
      setError('Missing homeowner information for this expense');
      return;
    }

    try {
      setRequestingPaymentExpenseId(expenseToRequestPayment.id);
      setError(null);
      const { data: authData } = await supabase.auth.getUser();
      const visibleLineItems = getVisibleChildLineItems(expenseToRequestPayment.id);

      await sendExpensePaymentRequestEmail({
        expenseId: expenseToRequestPayment.id,
        serviceRequestId: expenseToRequestPayment.service_request_id || undefined,
        homeownerId,
        homeownerName,
        serviceRequestTitle: expenseToRequestPayment.service_request?.title || null,
        expenseContext: getExpenseContextLabel(expenseToRequestPayment),
        expenseAmount: expenseToRequestPayment.amount,
        expenseDescription: expenseToRequestPayment.description,
        lineItems: visibleLineItems.map((lineItem) => ({
          amount: lineItem.amount,
          description: lineItem.description,
        })),
        requestedBy: authData.user?.id ?? null,
      });

      handleClosePaymentRequestConfirmModal();
      await fetchExpenses();
    } catch (requestError: any) {
      console.error('Error requesting homeowner payment:', requestError);
      setError(requestError?.message || 'Failed to request payment');
    } finally {
      setRequestingPaymentExpenseId(null);
    }
  };

  const resetCreateForm = () => {
    setCreateFormData({
      home_id: '',
      visit_id: '',
      amount: '',
      description: '',
      expense_date: new Date().toISOString().split('T')[0],
      category: '',
      vendor: '',
      billable_to_homeowner: true,
      needs_contractor_payment: true,
    });
  };

  const handleCreateExpense = async () => {
    const parsedAmount = parseFloat(createFormData.amount);
    if (!createFormData.home_id) {
      setError('Please select a home');
      return;
    }
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Amount must be greater than zero');
      return;
    }

    try {
      setCreatingExpense(true);
      setError(null);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const contextFields = buildExpenseContextFields({ type: 'home', homeId: createFormData.home_id });
      const payload = {
        ...contextFields,
        visit_id: createFormData.visit_id || null,
        amount: parsedAmount,
        description: createFormData.description || null,
        expense_date: createFormData.expense_date || null,
        category: createFormData.category || null,
        vendor: createFormData.vendor || null,
        parent_expense_id: null,
        quote_id: null,
        billable_to_homeowner: createFormData.billable_to_homeowner,
        needs_contractor_payment: createFormData.needs_contractor_payment,
        homeowner_visible: true,
        internal_cost: null,
        created_by: user.id,
      };

      const { error: createError } = await supabase
        .from('service_request_expenses')
        .insert(payload);
      if (createError) throw createError;

      setShowCreateModal(false);
      resetCreateForm();
      await fetchHomeowners();
      await fetchExpenses();
    } catch (createExpenseError: any) {
      setError(createExpenseError?.message || 'Failed to create expense');
    } finally {
      setCreatingExpense(false);
    }
  };

  const markAsNeedsInvoiced = async (expenseId: string) => {
    try {
      setUpdating(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: updateError } = await supabase
        .from('service_request_expenses')
        .update({
          invoice_requested: true,
          invoice_requested_at: new Date().toISOString(),
          invoice_requested_by: user.id,
        })
        .eq('id', expenseId);

      if (updateError) throw updateError;

      await fetchExpenses();
    } catch (err: any) {
      console.error('Error marking expense as needs invoiced:', err);
      setError(err.message || 'Failed to update expense');
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenHomeownerPaidModal = (expense: ExpenseWithRelations) => {
    setSelectedExpense(expense);
    setHomeownerPaymentFormData({
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'credit_card',
      payment_notes: '',
    });
    setShowHomeownerPaidModal(true);
  };

  const handleOpenContractorPaidModal = (expense: ExpenseWithRelations) => {
    setSelectedExpense(expense);
    setContractorPaymentFormData({
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'check',
      payment_notes: '',
    });
    setShowContractorPaidModal(true);
  };

  const handleCloseHomeownerPaidModal = () => {
    setShowHomeownerPaidModal(false);
    setSelectedExpense(null);
    setHomeownerPaymentFormData({
      payment_date: '',
      payment_method: 'credit_card',
      payment_notes: '',
    });
  };

  const handleCloseContractorPaidModal = () => {
    setShowContractorPaidModal(false);
    setSelectedExpense(null);
    setContractorPaymentFormData({
      payment_date: '',
      payment_method: 'check',
      payment_notes: '',
    });
  };

  const markHomeownerAsPaid = async () => {
    if (!selectedExpense) return;
    if (!homeownerPaymentFormData.payment_date) {
      setError('Please select a payment date');
      return;
    }

    try {
      setUpdating(true);
      setError(null);

      const { error: updateError } = await supabase
        .from('service_request_expenses')
        .update({
          is_paid: true,
          payment_date: homeownerPaymentFormData.payment_date,
          payment_method: homeownerPaymentFormData.payment_method,
          payment_notes: homeownerPaymentFormData.payment_notes || null,
        })
        .eq('id', selectedExpense.id);

      if (updateError) throw updateError;

      handleCloseHomeownerPaidModal();
      await fetchExpenses();
    } catch (err: any) {
      console.error('Error marking expense as paid:', err);
      setError(err.message || 'Failed to update expense');
    } finally {
      setUpdating(false);
    }
  };

  const markContractorAsPaid = async () => {
    if (!selectedExpense) return;
    if (!contractorPaymentFormData.payment_date) {
      setError('Please select a payment date');
      return;
    }

    try {
      setUpdating(true);
      setError(null);

      const { error: updateError } = await supabase
        .from('service_request_expenses')
        .update({
          contractor_paid: true,
          contractor_payment_date: contractorPaymentFormData.payment_date,
          contractor_payment_method: contractorPaymentFormData.payment_method,
          contractor_payment_notes: contractorPaymentFormData.payment_notes || null,
        })
        .eq('id', selectedExpense.id);

      if (updateError) throw updateError;

      const { error: childUpdateError } = await supabase
        .from('service_request_expenses')
        .update({
          contractor_paid: true,
          contractor_payment_date: contractorPaymentFormData.payment_date,
          contractor_payment_method: contractorPaymentFormData.payment_method,
          contractor_payment_notes: contractorPaymentFormData.payment_notes || null,
        })
        .eq('parent_expense_id', selectedExpense.id)
        .eq('needs_contractor_payment', true);

      if (childUpdateError) throw childUpdateError;

      handleCloseContractorPaidModal();
      await fetchExpenses();
    } catch (err: any) {
      console.error('Error marking contractor payment as paid:', err);
      setError(err.message || 'Failed to update expense');
    } finally {
      setUpdating(false);
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusBadgeStyle = (status: ExpenseStatus) => {
    switch (status) {
      case 'new':
        return { backgroundColor: '#dbeafe', color: '#1e40af' };
      case 'needs_invoiced':
        return { backgroundColor: '#fef3c7', color: '#92400e' };
      case 'awaiting_payment':
        return { backgroundColor: '#fee2e2', color: '#991b1b' };
      case 'homeowner_paid':
        return { backgroundColor: '#e0e7ff', color: '#3730a3' };
      case 'needs_contractor_payment':
        return { backgroundColor: '#ffedd5', color: '#9a3412' };
      case 'complete':
        return { backgroundColor: '#dcfce7', color: '#166534' };
      default:
        return { backgroundColor: '#f3f4f6', color: '#374151' };
    }
  };

  const getStatusLabel = (status: ExpenseStatus) => {
    switch (status) {
      case 'new':
        return 'New';
      case 'needs_invoiced':
        return 'Needs Invoiced';
      case 'awaiting_payment':
        return 'Awaiting Homeowner Payment';
      case 'homeowner_paid':
        return 'Homeowner Paid';
      case 'needs_contractor_payment':
        return 'Needs Contractor Payment';
      case 'complete':
        return 'Complete';
      default:
        return 'All';
    }
  };

  const getPaymentMethodLabel = (method: string | null) => {
    if (!method) return 'N/A';
    const labels: Record<string, string> = {
      'credit_card': 'Credit Card',
      'check': 'Check',
      'cash': 'Cash',
      'bank_transfer': 'Bank Transfer',
      'other': 'Other',
      'stripe': 'Stripe',
    };
    return labels[method] || method;
  };

  const toggleCardExpanded = (id: string) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getExpenseDetailUrl = (expense: ExpenseWithRelations) => {
    if (expense.service_request_id) {
      return `/admin/service-request/${expense.service_request_id}`;
    }
    if (expense.visit?.id) {
      return `/admin/visit?id=${expense.visit.id}`;
    }
    if (expense.home?.id) {
      return `/admin/home?id=${expense.home.id}`;
    }
    return null;
  };

  const getExpenseCompactTitle = (expense: ExpenseWithRelations) => {
    if (expense.vendor) return expense.vendor;
    if (expense.description) return expense.description;
    if (expense.service_request?.title) return expense.service_request.title;
    return getExpenseContextLabel(expense);
  };

  const renderExpenseContextLink = (expense: ExpenseWithRelations) => {
    if (expense.service_request && expense.service_request_id) {
      return Platform.OS === 'web' ? (
        <a
          href={`/admin/service-request/${expense.service_request_id}`}
          style={styles.serviceRequestLink as any}
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.button === 1) return;
            event.preventDefault();
            navigate(`/admin/service-request/${expense.service_request_id}`);
          }}
        >
          <ExternalLink size={14} color="#2563eb" />
          <Text style={styles.serviceRequestLinkText}>
            {expense.service_request.title}
          </Text>
        </a>
      ) : (
        <Pressable
          onPress={() => navigate(`/admin/service-request/${expense.service_request_id}`)}
          style={styles.serviceRequestLink}
        >
          <ExternalLink size={14} color="#2563eb" />
          <Text style={styles.serviceRequestLinkText}>
            {expense.service_request.title}
          </Text>
        </Pressable>
      );
    }

    if (!expense.service_request && expense.visit) {
      return Platform.OS === 'web' ? (
        <a
          href={`/admin/visit?id=${expense.visit.id}`}
          style={styles.serviceRequestLink as any}
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.button === 1) return;
            event.preventDefault();
            navigate(`/admin/visit?id=${expense.visit?.id}`);
          }}
        >
          <ExternalLink size={14} color="#2563eb" />
          <Text style={styles.serviceRequestLinkText}>
            {expense.visit.internal_name || 'Visit expense'}
          </Text>
        </a>
      ) : (
        <Pressable onPress={() => navigate(`/admin/visit?id=${expense.visit?.id}`)} style={styles.serviceRequestLink}>
          <ExternalLink size={14} color="#2563eb" />
          <Text style={styles.serviceRequestLinkText}>{expense.visit.internal_name || 'Visit expense'}</Text>
        </Pressable>
      );
    }

    if (!expense.service_request && !expense.visit && expense.home) {
      return Platform.OS === 'web' ? (
        <a
          href={`/admin/home?id=${expense.home.id}`}
          style={styles.serviceRequestLink as any}
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.button === 1) return;
            event.preventDefault();
            navigate(`/admin/home?id=${expense.home.id}`);
          }}
        >
          <ExternalLink size={14} color="#2563eb" />
          <Text style={styles.serviceRequestLinkText}>{getExpenseContextLabel(expense)}</Text>
        </a>
      ) : (
        <Pressable onPress={() => navigate(`/admin/home?id=${expense.home?.id}`)} style={styles.serviceRequestLink}>
          <ExternalLink size={14} color="#2563eb" />
          <Text style={styles.serviceRequestLinkText}>{getExpenseContextLabel(expense)}</Text>
        </Pressable>
      );
    }

    return null;
  };

  const renderExpenseDetails = (expense: ExpenseWithRelations) => (
    <View style={styles.expenseDetails}>
      {expense.vendor && (
        <View style={styles.expenseDetailRow}>
          <Building size={16} color="#6b7280" />
          <Text style={styles.expenseDetailText}>{expense.vendor}</Text>
        </View>
      )}
      {expense.category && (
        <View style={styles.expenseDetailRow}>
          <FileText size={16} color="#6b7280" />
          <Text style={styles.expenseDetailText}>{expense.category}</Text>
        </View>
      )}
      {expense.description && (
        <Text style={styles.expenseDescription}>{expense.description}</Text>
      )}
      {expense.internal_cost !== null && expense.internal_cost !== undefined && (
        <View style={styles.expenseDetailRow}>
          <DollarSign size={16} color="#6b7280" />
          <Text style={styles.expenseDetailText}>
            Internal cost: {formatCurrency(expense.internal_cost)} (margin: {formatCurrency(expense.amount - expense.internal_cost)})
          </Text>
        </View>
      )}
      {renderExpenseContextLink(expense)}
      {paymentRequestHistoryByExpenseId[expense.id]?.[0] && (
        <View style={styles.expenseDetailRow}>
          <Clock size={16} color="#6b7280" />
          <Text style={styles.expenseDetailText}>
            Last payment request:{' '}
            {new Date(paymentRequestHistoryByExpenseId[expense.id][0].requested_at).toLocaleString()} by{' '}
            {paymentRequestHistoryByExpenseId[expense.id][0].requester_name || 'Unknown'}
          </Text>
        </View>
      )}
      {paymentRequestHistoryByExpenseId[expense.id] && paymentRequestHistoryByExpenseId[expense.id].length > 0 && (
        <View style={styles.historyContainer}>
          <Text style={styles.historyTitle}>Payment Request History</Text>
          {paymentRequestHistoryByExpenseId[expense.id].slice(0, 3).map((historyItem) => (
            <Text key={historyItem.id} style={styles.historyItemText}>
              {new Date(historyItem.requested_at).toLocaleString()} - {historyItem.status} - {historyItem.requester_name || 'Unknown'}
            </Text>
          ))}
        </View>
      )}
      {expense.home && (
        <>
          {expense.home.owner && (
            <View style={styles.expenseDetailRow}>
              <User size={16} color="#6b7280" />
              <Text style={styles.expenseDetailText}>
                {expense.home.owner.full_name}
              </Text>
            </View>
          )}
          <View style={styles.expenseDetailRow}>
            <MapPin size={16} color="#6b7280" />
            <Text style={styles.expenseDetailText}>
              {expense.home.address}, {expense.home.city}, {expense.home.state}
            </Text>
          </View>
        </>
      )}
      <View style={styles.expenseDetailRow}>
        <CheckCircle2 size={16} color={expense.billable_to_homeowner ? (expense.is_paid ? '#16a34a' : '#dc2626') : '#6b7280'} />
        <Text style={styles.expenseDetailText}>
          Homeowner: {expense.billable_to_homeowner
            ? (expense.is_paid
              ? `Paid via ${getPaymentMethodLabel(expense.payment_method)} on ${formatDate(expense.payment_date)}`
              : (expense.invoice_id ? 'Awaiting payment on issued invoice' : 'Payment required'))
            : 'Not billable'}
        </Text>
      </View>
      <View style={styles.expenseDetailRow}>
        <CheckCircle2 size={16} color={expenseRequiresContractorPayment(expense) ? (isContractorPaid(expense) ? '#16a34a' : '#c2410c') : '#6b7280'} />
        <Text style={styles.expenseDetailText}>
          Contractor: {expenseRequiresContractorPayment(expense)
            ? (isContractorPaid(expense)
              ? `Paid via ${getPaymentMethodLabel(expense.contractor_payment_method)} on ${formatDate(expense.contractor_payment_date)}`
              : 'Payment required')
            : 'No contractor payment required'}
        </Text>
      </View>
      {getChildLineItems(expense.id).length > 0 && (
        <View style={styles.lineItemsContainer}>
          <Text style={styles.lineItemsTitle}>Line Items</Text>
          {getChildLineItems(expense.id).map((lineItem) => (
            <View key={lineItem.id} style={styles.lineItemRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.lineItemAmount}>
                  {formatCurrency(lineItem.amount)}
                  {lineItem.homeowner_visible ? '' : ' (hidden from homeowner)'}
                </Text>
                <Text style={styles.lineItemDescription}>
                  {lineItem.description || `${lineItem.category || 'Service'}${lineItem.vendor ? ` - ${lineItem.vendor}` : ''}`}
                </Text>
                {lineItem.needs_contractor_payment && (
                  <Text style={[styles.lineItemMeta, { color: lineItem.contractor_paid === true ? '#16a34a' : '#c2410c' }]}>
                    Contractor: {lineItem.contractor_paid === true ? 'Paid' : 'Payment required'}
                  </Text>
                )}
                {lineItem.internal_cost !== null && lineItem.internal_cost !== undefined && (
                  <Text style={styles.lineItemMeta}>
                    Internal cost: {formatCurrency(lineItem.internal_cost)}
                  </Text>
                )}
              </View>
            </View>
          ))}
        </View>
      )}
    </View>
  );

  const renderExpenseActions = (
    expense: ExpenseWithRelations,
    status: ExpenseStatus,
    canMarkNeedsInvoiced: boolean,
    canMarkPaid: boolean,
    compact = false,
  ) => (
    <View style={[styles.expenseActions, compact && styles.compactCardActions]}>
      {expense.billable_to_homeowner && !expense.is_paid && (
        <Pressable
          onPress={() => handleOpenPaymentRequestConfirmModal(expense)}
          disabled={requestingPaymentExpenseId === expense.id}
          style={styles.actionButton}
        >
          {requestingPaymentExpenseId === expense.id ? (
            <ActivityIndicator size="small" color="#2563eb" />
          ) : (
            <>
              <Send size={16} color="#2563eb" />
              <Text style={[styles.actionButtonText, { color: '#2563eb' }]}>
                Request Homeowner Payment
              </Text>
            </>
          )}
        </Pressable>
      )}
      {canMarkNeedsInvoiced && expense.billable_to_homeowner && (
        <Pressable
          onPress={() => markAsNeedsInvoiced(expense.id)}
          disabled={updating}
          style={styles.actionButton}
        >
          <FileText size={16} color="#92400e" />
          <Text style={[styles.actionButtonText, { color: '#92400e' }]}>
            Mark as Needs Invoiced
          </Text>
        </Pressable>
      )}
      {expense.billable_to_homeowner && !expense.is_paid && !expense.invoice_id && (
        <Pressable
          onPress={() => handleOpenCreateInvoiceModal(expense)}
          disabled={creatingInvoice}
          style={styles.actionButton}
        >
          <DollarSign size={16} color="#4338ca" />
          <Text style={[styles.actionButtonText, { color: '#4338ca' }]}>
            Create Invoice
          </Text>
        </Pressable>
      )}
      {canMarkPaid && expense.billable_to_homeowner && (
        <Pressable
          onPress={() => handleOpenHomeownerPaidModal(expense)}
          disabled={updating}
          style={styles.actionButton}
        >
          <CheckCircle2 size={16} color="#16a34a" />
          <Text style={[styles.actionButtonText, { color: '#16a34a' }]}>
            Mark Homeowner Paid
          </Text>
        </Pressable>
      )}
      {expenseNeedsContractorPayment(expense) && (
        <Pressable
          onPress={() => handleOpenContractorPaidModal(expense)}
          disabled={updating}
          style={styles.actionButton}
        >
          <CheckCircle2 size={16} color="#c2410c" />
          <Text style={[styles.actionButtonText, { color: '#c2410c' }]}>
            Mark Contractor Paid
          </Text>
        </Pressable>
      )}
      {status === 'complete' && (
        <View style={styles.paidIndicator}>
          <CheckCircle2 size={16} color="#16a34a" />
          <Text style={styles.paidText}>Complete</Text>
        </View>
      )}
    </View>
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#9ca3af" />
      </View>
    );
  }

  const visibleExpenses = statusFilter === 'needs_contractor_payment'
    ? expenses.filter((expense) => expenseNeedsContractorPayment(expense))
    : expenses;

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <DollarSign size={24} color="#111827" />
          <Text style={styles.title}>Expenses</Text>
        </View>
        <Pressable style={styles.createButton} onPress={() => setShowCreateModal(true)}>
          <Plus size={14} color="#ffffff" />
          <Text style={styles.createButtonText}>Create Expense</Text>
        </Pressable>
      </View>

      {/* Summary Cards */}
      <View style={styles.summaryContainer}>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryLabel}>New</Text>
          <Text style={styles.summaryAmount}>{formatCurrency(summaryStats.new)}</Text>
        </View>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryLabel}>Needs Invoiced</Text>
          <Text style={[styles.summaryAmount, { color: '#92400e' }]}>
            {formatCurrency(summaryStats.needs_invoiced)}
          </Text>
        </View>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryLabel}>Awaiting Homeowner</Text>
          <Text style={[styles.summaryAmount, { color: '#991b1b' }]}>
            {formatCurrency(summaryStats.awaiting_payment)}
          </Text>
        </View>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryLabel}>Homeowner Paid</Text>
          <Text style={[styles.summaryAmount, { color: '#3730a3' }]}>
            {formatCurrency(summaryStats.homeowner_paid)}
          </Text>
        </View>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryLabel}>Needs Contractor</Text>
          <Text style={[styles.summaryAmount, { color: '#9a3412' }]}>
            {formatCurrency(summaryStats.needs_contractor_payment)}
          </Text>
        </View>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryLabel}>Homeowner Outstanding</Text>
          <Text style={[styles.summaryAmount, { color: '#b91c1c' }]}>
            {formatCurrency(summaryStats.homeowner_outstanding)}
          </Text>
        </View>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryLabel}>Contractor Outstanding</Text>
          <Text style={[styles.summaryAmount, { color: '#c2410c' }]}>
            {formatCurrency(summaryStats.contractor_outstanding)}
          </Text>
        </View>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryLabel}>Complete</Text>
          <Text style={[styles.summaryAmount, { color: '#16a34a' }]}>
            {formatCurrency(summaryStats.complete)}
          </Text>
        </View>
        <View style={[styles.summaryCard, styles.summaryCardTotal]}>
          <Text style={styles.summaryLabel}>Total</Text>
          <Text style={[styles.summaryAmount, styles.summaryAmountTotal]}>
            {formatCurrency(summaryStats.total)}
          </Text>
        </View>
      </View>

      {/* Status Filter Tabs */}
      <View style={styles.filterContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.statusTabs}>
          {(['all', 'new', 'needs_invoiced', 'awaiting_payment', 'homeowner_paid', 'needs_contractor_payment', 'complete'] as ExpenseStatus[]).map((status) => {
            const isActive = statusFilter === status;
            const badgeStyle = getStatusBadgeStyle(status);
            return (
              <Pressable
                key={status}
                onPress={() => setStatusFilter(status)}
                style={[
                  styles.statusTab,
                  isActive && styles.statusTabActive,
                  isActive && { backgroundColor: badgeStyle.backgroundColor },
                ]}
              >
                <Text
                  style={[
                    styles.statusTabText,
                    isActive && { color: badgeStyle.color },
                    !isActive && styles.statusTabTextInactive,
                  ]}
                >
                  {getStatusLabel(status)}
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>
      </View>

      {/* Search and Filters */}
      <View style={styles.filtersRow}>
        <View style={styles.searchContainer}>
          <Search size={20} color="#6b7280" />
          <TextInput
            style={styles.searchInput}
            placeholder="Search by vendor, description, category, homeowner..."
            placeholderTextColor="#9ca3af"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery.length > 0 && (
            <Pressable
              onPress={() => {
                setSearchQuery('');
                fetchExpenses();
              }}
              style={styles.clearSearchButton}
            >
              <X size={16} color="#6b7280" />
            </Pressable>
          )}
        </View>
        
        {/* Homeowner Filter */}
        <View style={styles.homeownerFilterContainer}>
          {Platform.OS === 'web' ? (
            <select
              value={homeownerFilter || ''}
              onChange={(e) => setHomeownerFilter(e.target.value || null)}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 8,
                fontSize: 14,
                backgroundColor: '#ffffff',
                cursor: 'pointer',
                minWidth: 200,
              }}
            >
              <option value="">All Homeowners</option>
              {homeowners.map((homeowner) => (
                <option key={homeowner.id} value={homeowner.id}>
                  {homeowner.full_name}
                </option>
              ))}
            </select>
          ) : (
            <View style={styles.pickerContainer}>
              <Picker
                selectedValue={homeownerFilter || ''}
                onValueChange={(value) => setHomeownerFilter(value || null)}
                style={styles.picker}
              >
                <Picker.Item label="All Homeowners" value="" />
                {homeowners.map((homeowner) => (
                  <Picker.Item
                    key={homeowner.id}
                    label={homeowner.full_name}
                    value={homeowner.id}
                  />
                ))}
              </Picker>
            </View>
          )}
          {homeownerFilter && (
            <Pressable
              onPress={() => setHomeownerFilter(null)}
              style={styles.clearFilterButton}
            >
              <X size={16} color="#6b7280" />
            </Pressable>
          )}
        </View>

        <View style={styles.viewToggleRow}>
          <Pressable
            style={[styles.viewToggleButton, viewMode === 'default' && styles.viewToggleButtonActive]}
            onPress={() => setViewMode('default')}
          >
            <LayoutList size={14} color={viewMode === 'default' ? '#ffffff' : '#6b7280'} />
            <Text style={[styles.viewToggleButtonText, viewMode === 'default' && styles.viewToggleButtonTextActive]}>
              Full
            </Text>
          </Pressable>
          <Pressable
            style={[styles.viewToggleButton, viewMode === 'compact' && styles.viewToggleButtonActive]}
            onPress={() => setViewMode('compact')}
          >
            <List size={14} color={viewMode === 'compact' ? '#ffffff' : '#6b7280'} />
            <Text style={[styles.viewToggleButtonText, viewMode === 'compact' && styles.viewToggleButtonTextActive]}>
              Compact
            </Text>
          </Pressable>
        </View>
      </View>

      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      {/* Expenses List */}
      {visibleExpenses.length === 0 ? (
        <View style={styles.emptyContainer}>
          <DollarSign size={48} color="#9ca3af" />
          <Text style={styles.emptyTitle}>No Expenses Found</Text>
          <Text style={styles.emptyText}>
            {searchQuery || homeownerFilter 
              ? 'Try adjusting your search criteria or filters.' 
              : 'No expenses match the selected filters.'}
          </Text>
        </View>
      ) : (
        <WebScrollView style={[styles.expensesList, viewMode === 'compact' && styles.expensesListCompact]}>
          {visibleExpenses.map((expense) => {
            const status = getExpenseStatus(expense);
            const badgeStyle = getStatusBadgeStyle(status);
            const canMarkNeedsInvoiced = status === 'new';
            const canMarkPaid = status === 'awaiting_payment' || status === 'needs_invoiced';
            const isExpanded = expandedCards.has(expense.id);
            const detailUrl = getExpenseDetailUrl(expense);

            if (viewMode === 'compact') {
              return (
                <View key={expense.id} style={styles.compactCard}>
                  <Pressable
                    style={styles.compactCardHeader}
                    onPress={() => toggleCardExpanded(expense.id)}
                  >
                    <View style={styles.compactCardLeft}>
                      {isExpanded ? (
                        <ChevronDown size={14} color="#9ca3af" />
                      ) : (
                        <ChevronRight size={14} color="#9ca3af" />
                      )}
                      <View style={[styles.compactStatusBadge, { backgroundColor: badgeStyle.backgroundColor }]}>
                        <Text style={[styles.compactStatusText, { color: badgeStyle.color }]}>
                          {getStatusLabel(status)}
                        </Text>
                      </View>
                      <Text style={styles.compactAmount}>{formatCurrency(expense.amount)}</Text>
                      <Text style={styles.compactTitle} numberOfLines={1}>
                        {getExpenseCompactTitle(expense)}
                      </Text>
                      <Text style={styles.compactOwnerName} numberOfLines={1}>
                        {expense.home?.owner?.full_name || 'Unknown'}
                      </Text>
                    </View>
                    <View style={styles.compactCardRight}>
                      <Text style={styles.compactDate}>
                        {formatDate(expense.expense_date || expense.created_at)}
                      </Text>
                      {expense.billable_to_homeowner && !expense.is_paid && (
                        <Text style={styles.compactOutstandingFlag}>Due</Text>
                      )}
                      {detailUrl && (
                        <Pressable
                          style={styles.compactDetailsButton}
                          onPress={(e) => {
                            e.stopPropagation();
                            const nativeEvent = e.nativeEvent as unknown as MouseEvent;
                            if (nativeEvent.metaKey || nativeEvent.ctrlKey) {
                              window.open(detailUrl, '_blank');
                            } else {
                              navigate(detailUrl);
                            }
                          }}
                        >
                          <ExternalLink size={11} color="#ffffff" />
                        </Pressable>
                      )}
                    </View>
                  </Pressable>

                  {isExpanded && (
                    <View style={styles.compactCardBody}>
                      {renderExpenseDetails(expense)}
                      {renderExpenseActions(expense, status, canMarkNeedsInvoiced, canMarkPaid, true)}
                    </View>
                  )}
                </View>
              );
            }

            return (
              <View key={expense.id} style={styles.expenseCard}>
                <View style={styles.expenseHeader}>
                  <View style={styles.expenseHeaderLeft}>
                    <View style={[styles.statusBadge, { backgroundColor: badgeStyle.backgroundColor }]}>
                      <Text style={[styles.statusBadgeText, { color: badgeStyle.color }]}>
                        {getStatusLabel(status)}
                      </Text>
                    </View>
                    <Text style={styles.expenseAmount}>{formatCurrency(expense.amount)}</Text>
                  </View>
                  <Text style={styles.expenseDate}>{formatDate(expense.expense_date || expense.created_at)}</Text>
                </View>

                {renderExpenseDetails(expense)}
                {renderExpenseActions(expense, status, canMarkNeedsInvoiced, canMarkPaid)}
              </View>
            );
          })}
        </WebScrollView>
      )}

      <Modal
        visible={showCreateModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowCreateModal(false)}
      >
        <TouchableWithoutFeedback onPress={() => setShowCreateModal(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Create Expense</Text>
                  <Pressable onPress={() => setShowCreateModal(false)}>
                    <X size={24} color="#6b7280" />
                  </Pressable>
                </View>
                <View style={styles.modalBody}>
                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Home *</Text>
                    {Platform.OS === 'web' ? (
                      <select
                        value={createFormData.home_id}
                        onChange={(e) => setCreateFormData((prev) => ({ ...prev, home_id: e.target.value, visit_id: '' }))}
                        style={{ marginTop: 8, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 16 }}
                      >
                        <option value="">Select home</option>
                        {availableHomes.map((home) => (
                          <option key={home.id} value={home.id}>
                            {home.address}, {home.city}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <TextInput style={styles.input} value={createFormData.home_id} editable={false} />
                    )}
                  </View>
                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Visit (optional)</Text>
                    {Platform.OS === 'web' ? (
                      <select
                        value={createFormData.visit_id}
                        onChange={(e) => setCreateFormData((prev) => ({ ...prev, visit_id: e.target.value }))}
                        style={{ marginTop: 8, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 16 }}
                      >
                        <option value="">No visit</option>
                        {availableVisits.map((visit) => (
                          <option key={visit.id} value={visit.id}>
                            {visit.internal_name || visit.visit_type || 'Visit'} - {formatDate(visit.scheduled_date)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <TextInput style={styles.input} value={createFormData.visit_id} editable={false} />
                    )}
                  </View>
                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Amount *</Text>
                    <TextInput
                      style={styles.input}
                      value={createFormData.amount}
                      onChangeText={(text) => setCreateFormData((prev) => ({ ...prev, amount: text }))}
                      placeholder="0.00"
                      keyboardType="decimal-pad"
                    />
                  </View>
                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Description</Text>
                    <TextInput
                      style={styles.input}
                      value={createFormData.description}
                      onChangeText={(text) => setCreateFormData((prev) => ({ ...prev, description: text }))}
                      placeholder="Expense description"
                    />
                  </View>
                </View>
                <View style={styles.modalActions}>
                  <Pressable onPress={() => setShowCreateModal(false)} style={styles.cancelButton}>
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </Pressable>
                  <Pressable onPress={handleCreateExpense} style={[styles.saveButton, creatingExpense && styles.saveButtonDisabled]} disabled={creatingExpense}>
                    {creatingExpense ? <ActivityIndicator size="small" color="#ffffff" /> : <Text style={styles.saveButtonText}>Create</Text>}
                  </Pressable>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Create Invoice Modal */}
      <Modal
        visible={showCreateInvoiceModal}
        transparent
        animationType="fade"
        onRequestClose={handleCloseCreateInvoiceModal}
      >
        <TouchableWithoutFeedback onPress={handleCloseCreateInvoiceModal}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Create Invoice</Text>
                  <Pressable onPress={handleCloseCreateInvoiceModal}>
                    <X size={24} color="#6b7280" />
                  </Pressable>
                </View>

                {expenseToCreateInvoiceFor && (
                  <View style={styles.modalBody}>
                    <View style={styles.modalExpenseInfo}>
                      <Text style={styles.modalExpenseAmount}>
                        {formatCurrency(expenseToCreateInvoiceFor.amount)}
                      </Text>
                      <Text style={styles.modalExpenseVendor}>
                        {expenseToCreateInvoiceFor.home?.owner?.full_name || 'Homeowner'}
                      </Text>
                      <Text style={styles.modalExpenseDescription}>
                        {expenseToCreateInvoiceFor.description || getExpenseContextLabel(expenseToCreateInvoiceFor)}
                      </Text>
                      {getVisibleChildLineItems(expenseToCreateInvoiceFor.id).length > 0 && (
                        <View style={styles.modalLineItemsList}>
                          {getVisibleChildLineItems(expenseToCreateInvoiceFor.id).map((lineItem) => (
                            <Text key={lineItem.id} style={styles.modalLineItemText}>
                              - {lineItem.description || 'Line item'}: {formatCurrency(lineItem.amount)}
                            </Text>
                          ))}
                        </View>
                      )}
                    </View>

                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Due Date *</Text>
                      {Platform.OS === 'web' ? (
                        <input
                          type="date"
                          value={invoiceDueDate}
                          onChange={(e) => setInvoiceDueDate(e.target.value)}
                          style={{
                            marginTop: 8,
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: 8,
                            fontSize: 16,
                          }}
                        />
                      ) : (
                        <TextInput
                          style={styles.input}
                          value={invoiceDueDate}
                          onChangeText={setInvoiceDueDate}
                          placeholder="YYYY-MM-DD"
                          placeholderTextColor="#9ca3af"
                        />
                      )}
                    </View>

                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Invoice Notes (optional)</Text>
                      <TextInput
                        style={styles.textArea}
                        value={invoiceNotes}
                        onChangeText={setInvoiceNotes}
                        placeholder="Optional notes included on invoice"
                        placeholderTextColor="#9ca3af"
                        multiline
                        numberOfLines={3}
                      />
                    </View>

                    <View style={styles.modalActions}>
                      <Pressable onPress={handleCloseCreateInvoiceModal} style={styles.cancelButton}>
                        <Text style={styles.cancelButtonText}>Cancel</Text>
                      </Pressable>
                      <Pressable
                        onPress={handleCreateInvoice}
                        disabled={creatingInvoice || !invoiceDueDate}
                        style={[
                          styles.saveButton,
                          styles.invoiceButton,
                          (creatingInvoice || !invoiceDueDate) && styles.saveButtonDisabled,
                        ]}
                      >
                        {creatingInvoice ? (
                          <ActivityIndicator size="small" color="#ffffff" />
                        ) : (
                          <Text style={styles.saveButtonText}>Create Invoice</Text>
                        )}
                      </Pressable>
                    </View>
                  </View>
                )}
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Request Homeowner Payment Confirmation Modal */}
      <Modal
        visible={showPaymentRequestConfirmModal}
        transparent
        animationType="fade"
        onRequestClose={handleClosePaymentRequestConfirmModal}
      >
        <TouchableWithoutFeedback onPress={handleClosePaymentRequestConfirmModal}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Request Homeowner Payment</Text>
                  <Pressable onPress={handleClosePaymentRequestConfirmModal}>
                    <X size={24} color="#6b7280" />
                  </Pressable>
                </View>

                {expenseToRequestPayment && (
                  <View style={styles.modalBody}>
                    <View style={styles.modalExpenseInfo}>
                      <Text style={styles.modalExpenseAmount}>
                        {formatCurrency(expenseToRequestPayment.amount)}
                      </Text>
                      <Text style={styles.modalExpenseVendor}>
                        Send payment request to{' '}
                        {expenseToRequestPayment.home?.owner?.full_name || 'the homeowner'}?
                      </Text>
                      {expenseToRequestPayment.description && (
                        <Text style={styles.modalExpenseDescription}>
                          {expenseToRequestPayment.description}
                        </Text>
                      )}
                      {getVisibleChildLineItems(expenseToRequestPayment.id).length > 0 && (
                        <View style={styles.modalLineItemsList}>
                          {getVisibleChildLineItems(expenseToRequestPayment.id).map((lineItem) => (
                            <Text key={lineItem.id} style={styles.modalLineItemText}>
                              - {lineItem.description || 'Line item'}: {formatCurrency(lineItem.amount)}
                            </Text>
                          ))}
                        </View>
                      )}
                    </View>

                    <View style={styles.modalActions}>
                      <Pressable
                        onPress={handleClosePaymentRequestConfirmModal}
                        style={styles.cancelButton}
                      >
                        <Text style={styles.cancelButtonText}>Cancel</Text>
                      </Pressable>
                      <Pressable
                        onPress={handleConfirmRequestHomeownerPayment}
                        disabled={requestingPaymentExpenseId === expenseToRequestPayment.id}
                        style={[
                          styles.saveButton,
                          requestingPaymentExpenseId === expenseToRequestPayment.id && styles.saveButtonDisabled,
                        ]}
                      >
                        {requestingPaymentExpenseId === expenseToRequestPayment.id ? (
                          <ActivityIndicator size="small" color="#ffffff" />
                        ) : (
                          <Text style={styles.saveButtonText}>Send Payment Request</Text>
                        )}
                      </Pressable>
                    </View>
                  </View>
                )}
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Mark Homeowner as Paid Modal */}
      <Modal
        visible={showHomeownerPaidModal}
        transparent
        animationType="slide"
        onRequestClose={handleCloseHomeownerPaidModal}
      >
        <TouchableWithoutFeedback onPress={handleCloseHomeownerPaidModal}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Mark Homeowner as Paid</Text>
                  <Pressable onPress={handleCloseHomeownerPaidModal}>
                    <X size={24} color="#6b7280" />
                  </Pressable>
                </View>

                {selectedExpense && (
                  <View style={styles.modalBody}>
                    <View style={styles.modalExpenseInfo}>
                      <Text style={styles.modalExpenseAmount}>
                        {formatCurrency(selectedExpense.amount)}
                      </Text>
                      {selectedExpense.vendor && (
                        <Text style={styles.modalExpenseVendor}>{selectedExpense.vendor}</Text>
                      )}
                      {selectedExpense.description && (
                        <Text style={styles.modalExpenseDescription}>
                          {selectedExpense.description}
                        </Text>
                      )}
                    </View>

                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Payment Date *</Text>
                      {Platform.OS === 'web' ? (
                        <input
                          type="date"
                          value={homeownerPaymentFormData.payment_date}
                          onChange={(e) =>
                            setHomeownerPaymentFormData({ ...homeownerPaymentFormData, payment_date: e.target.value })
                          }
                          style={{
                            marginTop: 8,
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: 8,
                            fontSize: 16,
                          }}
                        />
                      ) : (
                        <TextInput
                          style={styles.input}
                          value={homeownerPaymentFormData.payment_date}
                          onChangeText={(text) =>
                            setHomeownerPaymentFormData({ ...homeownerPaymentFormData, payment_date: text })
                          }
                          placeholder="YYYY-MM-DD"
                          placeholderTextColor="#9ca3af"
                        />
                      )}
                    </View>

                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Payment Method *</Text>
                      {Platform.OS === 'web' ? (
                        <select
                          value={homeownerPaymentFormData.payment_method}
                          onChange={(e) =>
                            setHomeownerPaymentFormData({ ...homeownerPaymentFormData, payment_method: e.target.value })
                          }
                          style={{
                            marginTop: 8,
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: 8,
                            fontSize: 16,
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <option value="credit_card">Credit Card</option>
                          <option value="check">Check</option>
                          <option value="cash">Cash</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="other">Other</option>
                        </select>
                      ) : (
                        <View style={styles.pickerContainer}>
                          <TextInput
                            style={styles.input}
                            value={getPaymentMethodLabel(homeownerPaymentFormData.payment_method)}
                            editable={false}
                            placeholder="Select payment method"
                            placeholderTextColor="#9ca3af"
                          />
                        </View>
                      )}
                    </View>

                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Payment Notes</Text>
                      <TextInput
                        style={styles.textArea}
                        value={homeownerPaymentFormData.payment_notes}
                        onChangeText={(text) =>
                          setHomeownerPaymentFormData({ ...homeownerPaymentFormData, payment_notes: text })
                        }
                        placeholder="Optional notes about the payment"
                        placeholderTextColor="#9ca3af"
                        multiline
                        numberOfLines={3}
                      />
                    </View>

                    {error && (
                      <View style={styles.errorContainer}>
                        <Text style={styles.errorText}>{error}</Text>
                      </View>
                    )}

                    <View style={styles.modalActions}>
                      <Pressable
                        onPress={handleCloseHomeownerPaidModal}
                        style={styles.cancelButton}
                      >
                        <Text style={styles.cancelButtonText}>Cancel</Text>
                      </Pressable>
                      <Pressable
                        onPress={markHomeownerAsPaid}
                        disabled={updating || !homeownerPaymentFormData.payment_date}
                        style={[
                          styles.saveButton,
                          (updating || !homeownerPaymentFormData.payment_date) && styles.saveButtonDisabled,
                        ]}
                      >
                        {updating ? (
                          <ActivityIndicator size="small" color="#ffffff" />
                        ) : (
                          <Text style={styles.saveButtonText}>Mark Homeowner Paid</Text>
                        )}
                      </Pressable>
                    </View>
                  </View>
                )}
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Mark Contractor as Paid Modal */}
      <Modal
        visible={showContractorPaidModal}
        transparent
        animationType="slide"
        onRequestClose={handleCloseContractorPaidModal}
      >
        <TouchableWithoutFeedback onPress={handleCloseContractorPaidModal}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Mark Contractor as Paid</Text>
                  <Pressable onPress={handleCloseContractorPaidModal}>
                    <X size={24} color="#6b7280" />
                  </Pressable>
                </View>

                {selectedExpense && (
                  <View style={styles.modalBody}>
                    <View style={styles.modalExpenseInfo}>
                      <Text style={styles.modalExpenseAmount}>
                        {formatCurrency(selectedExpense.amount)}
                      </Text>
                      {selectedExpense.vendor && (
                        <Text style={styles.modalExpenseVendor}>{selectedExpense.vendor}</Text>
                      )}
                      {selectedExpense.description && (
                        <Text style={styles.modalExpenseDescription}>
                          {selectedExpense.description}
                        </Text>
                      )}
                    </View>

                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Payment Date *</Text>
                      {Platform.OS === 'web' ? (
                        <input
                          type="date"
                          value={contractorPaymentFormData.payment_date}
                          onChange={(e) =>
                            setContractorPaymentFormData({ ...contractorPaymentFormData, payment_date: e.target.value })
                          }
                          style={{
                            marginTop: 8,
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: 8,
                            fontSize: 16,
                          }}
                        />
                      ) : (
                        <TextInput
                          style={styles.input}
                          value={contractorPaymentFormData.payment_date}
                          onChangeText={(text) =>
                            setContractorPaymentFormData({ ...contractorPaymentFormData, payment_date: text })
                          }
                          placeholder="YYYY-MM-DD"
                          placeholderTextColor="#9ca3af"
                        />
                      )}
                    </View>

                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Payment Method *</Text>
                      {Platform.OS === 'web' ? (
                        <select
                          value={contractorPaymentFormData.payment_method}
                          onChange={(e) =>
                            setContractorPaymentFormData({ ...contractorPaymentFormData, payment_method: e.target.value })
                          }
                          style={{
                            marginTop: 8,
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: 8,
                            fontSize: 16,
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <option value="check">Check</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="credit_card">Credit Card</option>
                          <option value="cash">Cash</option>
                          <option value="other">Other</option>
                        </select>
                      ) : (
                        <TextInput
                          style={styles.input}
                          value={getPaymentMethodLabel(contractorPaymentFormData.payment_method)}
                          editable={false}
                          placeholder="Select payment method"
                          placeholderTextColor="#9ca3af"
                        />
                      )}
                    </View>

                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Payment Notes</Text>
                      <TextInput
                        style={styles.textArea}
                        value={contractorPaymentFormData.payment_notes}
                        onChangeText={(text) =>
                          setContractorPaymentFormData({ ...contractorPaymentFormData, payment_notes: text })
                        }
                        placeholder="Optional notes about the contractor payment"
                        placeholderTextColor="#9ca3af"
                        multiline
                        numberOfLines={3}
                      />
                    </View>

                    {error && (
                      <View style={styles.errorContainer}>
                        <Text style={styles.errorText}>{error}</Text>
                      </View>
                    )}

                    <View style={styles.modalActions}>
                      <Pressable
                        onPress={handleCloseContractorPaidModal}
                        style={styles.cancelButton}
                      >
                        <Text style={styles.cancelButtonText}>Cancel</Text>
                      </Pressable>
                      <Pressable
                        onPress={markContractorAsPaid}
                        disabled={updating || !contractorPaymentFormData.payment_date}
                        style={[
                          styles.saveButton,
                          (updating || !contractorPaymentFormData.payment_date) && styles.saveButtonDisabled,
                        ]}
                      >
                        {updating ? (
                          <ActivityIndicator size="small" color="#ffffff" />
                        ) : (
                          <Text style={styles.saveButtonText}>Mark Contractor Paid</Text>
                        )}
                      </Pressable>
                    </View>
                  </View>
                )}
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  createButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#111827',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  createButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  summaryContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
  },
  summaryCard: {
    flex: 1,
    minWidth: 120,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  summaryCardTotal: {
    backgroundColor: '#111827',
    borderColor: '#111827',
  },
  summaryLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: '#6b7280',
    marginBottom: 4,
  },
  summaryAmount: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111827',
  },
  summaryAmountTotal: {
    color: '#ffffff',
  },
  filterContainer: {
    marginBottom: 16,
  },
  statusTabs: {
    flexDirection: 'row',
    gap: 8,
  },
  statusTab: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  statusTabActive: {
    borderColor: 'transparent',
  },
  statusTabText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  statusTabTextInactive: {
    color: '#6b7280',
  },
  filtersRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  searchContainer: {
    flex: 1,
    minWidth: 200,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#111827',
  },
  clearSearchButton: {
    padding: 4,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  homeownerFilterContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    ...(Platform.OS === 'web' && {
      minWidth: 200,
    }),
  },
  pickerContainer: {
    flex: 1,
    minWidth: 200,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  },
  picker: {
    height: 40,
  },
  clearFilterButton: {
    padding: 4,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  errorContainer: {
    padding: 12,
    backgroundColor: '#fef2f2',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#fecaca',
    marginBottom: 16,
  },
  errorText: {
    color: '#dc2626',
    fontSize: 14,
  },
  emptyContainer: {
    padding: 60,
    alignItems: 'center',
    gap: 16,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  emptyText: {
    fontSize: 16,
    color: '#6b7280',
    textAlign: 'center',
  },
  expensesList: {
    flex: 1,
  },
  expensesListCompact: {
    gap: 0,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    overflow: 'hidden',
  },
  viewToggleRow: {
    flexDirection: 'row',
    gap: 0,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    overflow: 'hidden',
    alignSelf: 'flex-start',
  },
  viewToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: '#ffffff',
  },
  viewToggleButtonActive: {
    backgroundColor: '#111827',
  },
  viewToggleButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#6b7280',
  },
  viewToggleButtonTextActive: {
    color: '#ffffff',
  },
  compactCard: {
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  compactCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
  },
  compactCardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  compactStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    flexShrink: 0,
  },
  compactStatusText: {
    fontSize: 11,
    fontWeight: '600',
  },
  compactAmount: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
    flexShrink: 0,
  },
  compactTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
    flex: 1,
    minWidth: 0,
  },
  compactOwnerName: {
    fontSize: 12,
    color: '#6b7280',
    flexShrink: 0,
    maxWidth: 140,
  },
  compactCardRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  compactDate: {
    fontSize: 12,
    color: '#6b7280',
  },
  compactOutstandingFlag: {
    fontSize: 11,
    color: '#dc2626',
    fontWeight: '600',
  },
  compactDetailsButton: {
    width: 24,
    height: 24,
    borderRadius: 4,
    backgroundColor: '#111827',
    justifyContent: 'center',
    alignItems: 'center',
  },
  compactCardBody: {
    paddingHorizontal: 16,
    paddingBottom: 12,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
    backgroundColor: '#fafafa',
    gap: 10,
  },
  compactCardActions: {
    paddingTop: 0,
    borderTopWidth: 0,
    marginTop: 4,
  },
  expenseCard: {
    backgroundColor: '#f9fafb',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
    marginBottom: 12,
    gap: 12,
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
    }),
  },
  expenseHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  expenseHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  expenseAmount: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#111827',
  },
  expenseDate: {
    fontSize: 14,
    color: '#6b7280',
  },
  expenseDetails: {
    gap: 8,
  },
  expenseDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  expenseDetailText: {
    fontSize: 14,
    color: '#374151',
  },
  expenseDescription: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 4,
  },
  lineItemsContainer: {
    marginTop: 8,
    padding: 10,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    backgroundColor: '#f9fafb',
    gap: 6,
  },
  lineItemsTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
  },
  lineItemRow: {
    padding: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 6,
    backgroundColor: '#ffffff',
  },
  lineItemAmount: {
    fontSize: 12,
    fontWeight: '600',
    color: '#111827',
  },
  lineItemDescription: {
    marginTop: 2,
    fontSize: 12,
    color: '#4b5563',
  },
  lineItemMeta: {
    marginTop: 4,
    fontSize: 11,
    color: '#6b7280',
  },
  serviceRequestLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
    ...(Platform.OS === 'web' && {
      display: 'inline-flex',
      cursor: 'pointer',
    }),
  },
  serviceRequestLinkText: {
    fontSize: 14,
    color: '#2563eb',
    fontWeight: '500',
  },
  historyContainer: {
    marginTop: 4,
    paddingLeft: 2,
    gap: 2,
  },
  historyTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#4b5563',
  },
  historyItemText: {
    fontSize: 12,
    color: '#6b7280',
  },
  expenseActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  paidIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  paidText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#16a34a',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    width: '100%',
    maxWidth: 500,
    maxHeight: '90%',
    ...(Platform.OS === 'web' && {
      ...shadows.lg,
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#111827',
  },
  modalBody: {
    padding: 20,
  },
  modalExpenseInfo: {
    marginBottom: 20,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalExpenseAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 8,
  },
  modalExpenseVendor: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 4,
  },
  modalExpenseDescription: {
    fontSize: 14,
    color: '#6b7280',
  },
  modalLineItemsList: {
    marginTop: 8,
    gap: 4,
  },
  modalLineItemText: {
    fontSize: 13,
    color: '#4b5563',
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  input: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 16,
    color: '#111827',
    backgroundColor: '#ffffff',
  },
  textArea: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 16,
    color: '#111827',
    backgroundColor: '#ffffff',
    minHeight: 80,
    textAlignVertical: 'top',
  },
  pickerContainer: {
    marginTop: 8,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 24,
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
  },
  saveButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 24,
    backgroundColor: '#16a34a',
    borderRadius: 8,
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  invoiceButton: {
    backgroundColor: '#4338ca',
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
});

export default ExpensesTab;

